import React, { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, X } from "lucide-react";
import type { Reserva, ReservationStatus } from "../../hooks/useApi";
import useApi from "../../hooks/useApi";

type DuplicateReservation = {
  id: number;
  fecha: string;
  clientName: string;
  finalClientName: string | null;
  adultos: number;
  ninios: number;
  hotel: string;
  status: ReservationStatus;
  excursion: string;
};

interface DuplicateReservationCheckProps {
  name: string;
  date: string;
  hotel?: string;
  adultos?: number;
  ninios?: number;
  onShowDetails?: (reservationId: number) => void;
  type?: "b2c" | "b2b"; // To differentiate between client types
}

// Función para formatear fechas ISO
function formatDate(dateString: string): string {
  if (!dateString) return "";
  
  try {
    const date = new Date(dateString);
    
    // Comprobar si la fecha es válida
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    // Formatear como DD/MM/YYYY a las HH:MM
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} a las ${hours}:${minutes}`;
  } catch (error) {
    console.error("Error al formatear fecha:", error);
    return dateString;
  }
}

// Función para formatear el nombre de hotel con seguridad
function formatHotel(hotel: string): string {
  if (!hotel) return "No especificado";
  
  // Si el hotel es muy largo, acortarlo
  if (hotel.length > 40) {
    return hotel.substring(0, 37) + "...";
  }
  
  return hotel;
}

export default function DuplicateReservationCheck({
  name,
  date,
  hotel,
  adultos,
  ninios,
  onShowDetails,
  type
}: DuplicateReservationCheckProps) {
  const { checkDuplicateReservations } = useApi();
  const [duplicates, setDuplicates] = useState<DuplicateReservation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create stable references to the props to prevent unnecessary re-renders
  const nameRef = React.useRef(name);
  const dateRef = React.useRef(date);
  const hotelRef = React.useRef(hotel);
  const adultosRef = React.useRef(adultos);
  const niniosRef = React.useRef(ninios);

  // Update refs when props change
  React.useEffect(() => {
    nameRef.current = name;
    dateRef.current = date;
    hotelRef.current = hotel;
    adultosRef.current = adultos;
    niniosRef.current = ninios;
  }, [name, date, hotel, adultos, ninios]);

  // Use a dedicated state to trigger the duplicate check
  const [shouldCheck, setShouldCheck] = useState(false);

  // Effect to reset state and trigger check when essential props change
  React.useEffect(() => {
    // Reset when inputs change
    setHasAcknowledged(false);
    setError(null);
    
    // Don't check if essential fields are missing
    if (!name || !date) {
      setDuplicates([]);
      return;
    }
    
    // Debounce the check
    const debounceId = setTimeout(() => {
      setShouldCheck(true);
    }, 800);

    return () => clearTimeout(debounceId);
  }, [name, date, hotel, adultos, ninios]);

  // Separate effect to perform the actual API check
  React.useEffect(() => {
    // Only run this effect when shouldCheck is true
    if (!shouldCheck) return;

    const checkForDuplicates = async () => {
      try {
        setIsLoading(true);
        
        // Build query parameters
        const params: Record<string, string> = {
          name: nameRef.current,
          date: dateRef.current,
        };
        
        if (hotelRef.current) params.hotel = hotelRef.current;
        if (adultosRef.current !== undefined) params.adultos = adultosRef.current.toString();
        if (niniosRef.current !== undefined) params.ninios = niniosRef.current.toString();
        
        const result = await checkDuplicateReservations(params);
        setDuplicates(result.data || []);
      } catch (err) {
        console.error("Error checking for duplicate reservations:", err);
        setError("No se pudieron verificar reservas duplicadas");
        setDuplicates([]);
      } finally {
        setIsLoading(false);
        setShouldCheck(false);  // Reset the trigger
      }
    };

    checkForDuplicates();
  }, [shouldCheck, checkDuplicateReservations]);

  const showWarningPanel = !(duplicates.length === 0 && !isLoading && !error) && !hasAcknowledged;

  if (!showWarningPanel) {
    return null;
  }

  return (
    <div className="mt-6 mb-6 sm:mx-0 mx-1">
      <div className="rounded-lg border border-amber-200 bg-amber-50 shadow-sm dark:border-amber-800/60 dark:bg-amber-900/20">
        <div className="px-4 py-3 border-b border-amber-200 dark:border-amber-800/60 flex justify-between items-center">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-amber-500 dark:text-amber-400 mr-2.5" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {isLoading 
                ? "Verificando reservas similares..." 
                : error 
                  ? "Error de verificación" 
                  : "Posibles reservas duplicadas encontradas"}
            </h3>
          </div>
          <button 
            onClick={() => setHasAcknowledged(true)}
            className="text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 p-1 rounded-full hover:bg-amber-100 dark:hover:bg-amber-800/30 transition-colors"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        
        <div className="p-4">
          {isLoading ? (
            <div className="py-3 text-sm text-amber-700 dark:text-amber-300 flex items-center justify-center">
              <div className="animate-pulse mr-2 h-2.5 w-2.5 rounded-full bg-amber-500"></div>
              Consultando información...
            </div>
          ) : error ? (
            <div className="py-3 text-sm text-amber-700 dark:text-amber-300 flex items-center">
              <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" aria-hidden="true" />
              {error}
            </div>
          ) : (
            <>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                Hemos encontrado <span className="font-semibold">{duplicates.length}</span> reserva{duplicates.length !== 1 ? 's' : ''} similar{duplicates.length !== 1 ? 'es' : ''} que podría{duplicates.length !== 1 ? 'n' : ''} considerarse duplicada{duplicates.length !== 1 ? 's' : ''}:
              </p>
              
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {duplicates.map(dup => (
                  <div
                    key={dup.id}
                    className="p-3 rounded-md bg-white dark:bg-gray-800/90 border border-gray-100 dark:border-gray-700 shadow-sm transition-all hover:shadow-md"
                  >
                    <div className="mb-2 flex justify-between items-start">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        #{dup.id}: {dup.excursion}
                      </div>
                      
                      {onShowDetails && (
                        <button
                          onClick={() => onShowDetails(dup.id)}
                          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center ml-2"
                        >
                          Ver detalles
                          <ExternalLink className="ml-1 h-3 w-3" />
                        </button>
                      )}
                    </div>
                    
                    <div className="text-sm">
                      <div className="flex flex-wrap -mx-2">
                        <div className="px-2 w-full sm:w-1/2">
                          <div className="mb-1">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Cliente:</span>{" "}
                            <span className="text-gray-900 dark:text-white">{dup.clientName}</span>
                            {dup.finalClientName && (
                              <div className="ml-4 text-xs text-gray-500">
                                <span className="italic">Final: {dup.finalClientName}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="px-2 w-full sm:w-1/2">
                          <div className="mb-1">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Fecha:</span>{" "}
                            <span className="text-gray-900 dark:text-white">{formatDate(dup.fecha)}</span>
                          </div>
                        </div>
                        
                        <div className="px-2 w-full sm:w-1/2">
                          <div className="mb-1">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Hotel:</span>{" "}
                            <span className="text-gray-900 dark:text-white">{formatHotel(dup.hotel)}</span>
                          </div>
                        </div>
                        
                        <div className="px-2 w-full sm:w-1/2">
                          <div className="mb-1">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Personas:</span>{" "}
                            <span className="text-gray-900 dark:text-white">
                              {dup.adultos + dup.ninios} ({dup.adultos} {dup.adultos === 1 ? 'adulto' : 'adultos'}
                              {dup.ninios > 0 && <>, {dup.ninios} {dup.ninios === 1 ? 'niño' : 'niños'}</>})
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-5 pt-4 border-t border-amber-200 dark:border-amber-800/60 flex flex-col sm:flex-row justify-between items-center gap-4">
                <p className="text-sm text-amber-700 dark:text-amber-300 text-center sm:text-left">
                  Por favor, revise estas reservas antes de continuar.
                </p>
                <div className="flex items-center">
                  <button
                    onClick={() => setHasAcknowledged(true)}
                    className="rounded-md bg-amber-200 dark:bg-amber-800/50 px-4 py-2 text-sm font-medium text-amber-800 dark:text-amber-300 hover:bg-amber-300 dark:hover:bg-amber-700/50 transition-colors shadow-sm"
                  >
                    He revisado estas reservas
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
} 